/**
 * Landing page for the setup link in the welcome email.
 *
 *   https://app.grouppulsehq.com/connect-extension?token=<setup_jwt>
 *
 * Important: this page deliberately does NOT call `/extension/setup` itself.
 * The extension's options page does that — it takes either the raw setup
 * token or the full URL and POSTs to /extension/setup, then stores the
 * returned long-lived JWT in `chrome.storage.local`. If we called the
 * endpoint from a regular web page, the resulting JWT would have nowhere
 * to live (chrome.storage is only accessible to extensions).
 *
 * So the page's job is purely:
 *   1. Show the user the setup URL clearly + a copy affordance.
 *   2. Walk them through pasting it into the extension's options page.
 *   3. Provide a download link for the extension if they don't have it yet.
 *
 * The setup token is JWT-signed with a 15-min expiry — if the user comes
 * here outside that window we'll show them an "expired link" message and
 * ask them to email Michael for a new one.
 */

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const EXTENSION_DOWNLOAD_URL =
  'https://grouppulsehq.com/download/grouppulse-latest.zip';

export function ConnectExtension() {
  const [search] = useSearchParams();
  const token = search.get('token');

  // Decode the JWT payload (no signature check — that's the API's job)
  // purely so we can detect an obviously-expired token client-side and
  // show a friendlier error before they paste it into the extension.
  const tokenStatus = useMemo(() => analyzeToken(token), [token]);

  // Reconstruct the URL the extension expects. The extension accepts EITHER
  // a raw token OR the full URL — we display the URL because that's what
  // the email links to, and pasting URLs is more familiar than pasting JWTs.
  const fullSetupUrl = token
    ? `https://app.grouppulsehq.com/connect-extension?token=${token}`
    : '';

  async function copyUrl() {
    try {
      await navigator.clipboard.writeText(fullSetupUrl);
    } catch {
      // Older browsers — the URL is selectable in the code box as a fallback.
    }
  }

  if (!token) {
    return (
      <div className="page-centered">
        <div className="card card-narrow">
          <h1>Missing setup link</h1>
          <p className="muted">
            This page expects a <code>?token=…</code> parameter on the URL —
            the welcome email you received has the right one. If you can&apos;t
            find it, email{' '}
            <a href="mailto:michael@mach10agency.com">michael@mach10agency.com</a>
            {' '}and we&apos;ll issue you a fresh setup link.
          </p>
        </div>
      </div>
    );
  }

  if (tokenStatus === 'expired') {
    return (
      <div className="page-centered">
        <div className="card card-narrow">
          <h1>Setup link expired</h1>
          <p className="muted">
            These links are valid for 15 minutes for security. Email{' '}
            <a href="mailto:michael@mach10agency.com">michael@mach10agency.com</a>{' '}
            and we&apos;ll send you a fresh one — it&apos;s a 30-second job on our end.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-centered" style={{ alignItems: 'flex-start' }}>
      <div
        className="card"
        style={{ maxWidth: 640, width: '100%', margin: '48px auto' }}
      >
        <h1 style={{ marginBottom: 4 }}>Connect your Chrome extension</h1>
        <p className="muted" style={{ marginTop: 0 }}>
          Two short steps and you&apos;re live. The whole thing takes about a
          minute.
        </p>

        <Step
          number="1"
          title="Install the GroupPulse extension"
          done={false}
        >
          <p>
            If you don&apos;t have it yet, download the ZIP, unzip it, and load
            it into Chrome via{' '}
            <code>chrome://extensions</code> → <strong>Developer mode</strong>{' '}
            (toggle in top-right) → <strong>Load unpacked</strong>.
          </p>
          <a
            className="btn btn-primary"
            href={EXTENSION_DOWNLOAD_URL}
            target="_blank"
            rel="noreferrer"
          >
            Download extension (.zip)
          </a>
        </Step>

        <Step
          number="2"
          title="Paste this setup link into the extension"
          done={false}
        >
          <p>
            Click the GroupPulse icon in your Chrome toolbar →{' '}
            <strong>Options</strong> (the gear icon, or right-click the
            extension and choose <strong>Options</strong>). Paste this link
            into the &quot;Setup link or one-time token&quot; field and click{' '}
            <strong>Connect</strong>.
          </p>
          <div style={urlBoxStyle}>{fullSetupUrl}</div>
          <button className="btn btn-primary" onClick={copyUrl}>
            Copy setup link
          </button>
        </Step>

        <div
          style={{
            marginTop: 32,
            paddingTop: 24,
            borderTop: '1px solid var(--border)',
            color: 'var(--muted)',
            fontSize: 14,
          }}
        >
          Once connected, the extension uploads any leads it captures every 60
          seconds, and your dashboard at{' '}
          <Link to="/dashboard">app.grouppulsehq.com/dashboard</Link> will fill
          in as soon as you&apos;re signed in. The first sign-in uses the email
          address from your subscription.
        </div>
      </div>
    </div>
  );
}

function Step({
  number,
  title,
  done,
  children,
}: {
  number: string;
  title: string;
  done: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 24,
        paddingTop: 24,
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: done ? '#10b981' : 'var(--primary)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {done ? '✓' : number}
        </div>
        <h2 style={{ margin: 0, fontSize: 18 }}>{title}</h2>
      </div>
      <div style={{ marginLeft: 40 }}>{children}</div>
    </section>
  );
}

// Read the JWT's `exp` claim without verifying the signature — purely so the
// page can render a friendlier "expired" message instead of letting the
// extension's POST /extension/setup reject with a 401.
function analyzeToken(token: string | null): 'ok' | 'expired' | 'malformed' {
  if (!token) return 'malformed';
  const parts = token.split('.');
  if (parts.length !== 3) return 'malformed';
  try {
    const payloadJson = atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(payloadJson) as { exp?: number };
    if (typeof payload.exp === 'number' && payload.exp * 1000 < Date.now()) {
      return 'expired';
    }
    return 'ok';
  } catch {
    return 'malformed';
  }
}

const urlBoxStyle: React.CSSProperties = {
  background: '#f1f5f9',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '12px 14px',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: 12,
  wordBreak: 'break-all',
  margin: '12px 0 16px',
  userSelect: 'all',
};
